// Unpublished Work © 2023-2024 Deere & Company.

import { useOktaAuth } from "@okta/okta-react";
import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router";
import { ConfigContext } from "./ConfigContext";
import { RBACAccessContext } from "./RBACAccessContext/RBACAccessContext";
import { UserContext } from "./UserContext/UserContext";
import { Account } from "../api/dealer/DealerApi";
import { DealerAccount } from "../api/dealer/types";
import { RBACRole } from "../api/rbac/types";
import useXtremeBffApi from "../api/xtremeBff/useXtremeBff";
import xtremeBffApi from "../api/xtremeBff/xtremeBffApi";
import LoadingIndicator from "../components/LoadingIndicator/LoadingIndicator";
import i18n from "../i18n/i18n";
import { isAuthorizedForMultipleAccounts } from "../pages/preferences/utils";
import LocalStorage from "../utils/LocalStorage";
import SessionStorage from "../utils/SessionStorage";
import { getAccountIdFromUrl } from "../utils/urlFormatters";

/*
  To complete the migration complete the following steps and update the
   usages of this context.

   Important places to update
   - Header.tsx
   - render.tsx
   - renderHook.tsx
   - PartsInfo.tsx
   - PartsInfoContent.tsx

   Remove the feature toggle ENABLE_DEALER_CONTEXT_V2 from ConfigContext.tsx

   Also move this context in its own directory
*/

/* To remove v1 implementation, remove `setDealerLocation` and
 `dealerLocation` from the value and this interface. */

interface DealerAccountErrorDetails {
  dealerInfoLoaderResponseStatus: number | undefined;
  invalidDealerAccountId: string | undefined;
  errorOccurredDealerIds: string[] | undefined;
  blockedDealerAccountId: boolean;
}

export interface DealerAccountContextProps {
  dealerAccount?: DealerAccount;
  dealerInfoLoaderResponseStatus?: number;
  isErrorOccurred?: boolean;
  isAuthErrorOccurred?: boolean;
  noAccountsMappedError?: boolean;
  reloadDealerAccount?: (accountId?: string | string[]) => void;
  setDealerLocation: (location: string) => void | undefined;
  dealerLocation: string | undefined;
  selectedAccounts: DealerAccount[];
  updateSelectedAccounts: (accounts: DealerAccount[]) => void;
  mappedAccounts: DealerAccount[];
  updateMappedAccounts: (accountIds: string[]) => void;
  corporateManagerSetupIncomplete: boolean;
  invalidDealerAccountId?: string;
  errorOccurredDealerIds?: string[];
  isAccountChanged?: boolean | undefined;
  setIsAccountChanged: (value: boolean) => void | undefined;
  overAllBlock: boolean;
  underEmbargo: string;
  dealerInvalid: boolean;
  blockerDealerAccountId?: boolean;
  allAccounts: DealerAccount[];
  initialUrlAccountId?: string;
}

export const DealerAccountContext = createContext(
  {} as DealerAccountContextProps
);

export const dealerInfoLoader = (
  SERVICE_URL: string,
  accountIds: string[],
  accessToken: string,
  lang: string,
  ENABLE_ACCOUNT_LOCATION_FALLBACK: boolean,
  isDealerApiAuthEnabled: boolean,
  xtremeBffApiHook: ReturnType<typeof useXtremeBffApi>
): Promise<DealerAccount[]> =>
  isDealerApiAuthEnabled
    ? xtremeBffApiHook
        .getAccountsDetails(SERVICE_URL, accountIds, lang)
        .then((data) =>
          data.map((acc) => {
            if (!acc.location?.country && ENABLE_ACCOUNT_LOCATION_FALLBACK) {
              return {
                ...acc,
                location: { ...acc.location, country: "BR" },
              };
            }
            return acc;
          })
        )
    : xtremeBffApi
        .getAccountsDetails(SERVICE_URL, accountIds, accessToken, lang)
        .then((data) =>
          data.map((acc) => {
            if (!acc.location?.country && ENABLE_ACCOUNT_LOCATION_FALLBACK) {
              return {
                ...acc,
                location: { ...acc.location, country: "BR" },
              };
            }
            return acc;
          })
        );
const reorderByAccountsIds =
  (accountIds: string[]) =>
  (response: DealerAccount[]): DealerAccount[] => {
    const dealerAccountMap = new Map<string, DealerAccount>(
      response.map((acc) => [acc.id.toUpperCase(), acc])
    );
    return accountIds.map((id) =>
      dealerAccountMap.get(id.toUpperCase())
    ) as DealerAccount[];
  };

export const DealerAccountProvider = ({
  children,
  initialValue = [],
  isErrorOccurred: isErrorOccurredInInitialCall = false,
  error: errorOccurredInInitialCall,
}: {
  children: ReactElement;
  initialValue?: any;
  isErrorOccurred?: boolean;
  error?: any;
}) => {
  const bffApiHook = useXtremeBffApi();

  // Remove `ENABLE_DEALER_CONTEXT_V2` to complete migration to v2
  const {
    ENABLE_DEALER_CONTEXT_V2,
    ENABLE_ACCOUNT_LOCATION_FALLBACK,
    XTREME_BFF_SERVICE_URL,
    ENABLE_MULTIPLE_ACCOUNTS,
    ENABLE_MULTIPLE_ACCOUNTS_SELECTION_DROPDOWN,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_CORPORATE_VIEW_DPA_ROLE,
    ENABLE_V3_SALES_DETAILS_INTEGRATION,
  } = useContext(ConfigContext);
  const { isRBACEnabled, isCorporateViewEnabled } =
    useContext(RBACAccessContext);
  const user = useContext(UserContext);

  const { pathname, search } = useLocation();
  const { authState } = useOktaAuth();

  const isDealerApiAuthEnabled =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE;

  const initialDealerAccount =
    isCorporateViewEnabled &&
    isAuthorizedForMultipleAccounts(
      user,
      ENABLE_MULTIPLE_ACCOUNTS,
      ENABLE_CORPORATE_VIEW_DPA_ROLE
    )
      ? undefined
      : initialValue[0];

  const [isErrorOccurred, setIsErrorOccurred] = useState(
    isErrorOccurredInInitialCall
  );

  const [
    dealerInfoResponseAndInvalidDealerAccount,
    setDealerInfoResponseAndInvalidDealerAccount,
  ] = useState<DealerAccountErrorDetails>({
    dealerInfoLoaderResponseStatus: initialValue[0] ? 200 : undefined,
    invalidDealerAccountId:
      errorOccurredInInitialCall?.response?.status === 404 ? "404" : undefined,
    errorOccurredDealerIds: undefined,
    blockedDealerAccountId: false,
  });

  const [hasAccessRestriction, setHasAccessRestriction] =
    useState<boolean>(false);
  const [embargoStatus, setEmbargoStatus] = useState<string>("");
  const [isDealerInvalid, setIsDealerInvalid] = useState<boolean>(false);
  const [initialUrlAccountId, setInitialUrlAccountId] = useState<
    string | undefined
  >();

  const [isAuthErrorOccurred, setIsAuthErrorOccurred] = useState(false);
  const [noAccountsMappedError, setNoAccountsMappedError] = useState(false);
  const [dealerAccountInfo, setDealerAccountInfo] = useState({
    selectedAccounts: [] as DealerAccount[],
    mappedAccounts: [] as DealerAccount[],
    dealerAccount: initialDealerAccount as DealerAccount | undefined,
    dealerLocation: initialDealerAccount?.location?.country,
  });
  const [isAccountChanged, setIsAccountChanged] = useState(false);
  // Remove this to complete migration to v2
  const setDealerLocation = (location: string) => {
    setDealerAccountInfo({
      ...dealerAccountInfo,
      dealerLocation: location,
    });
  };
  const userHasAccessToAccount = (accountID: string) =>
    user?.accounts.some((account) => account.id === accountID);

  const areAccountsInfoSame = (
    existingAcc: DealerAccount[],
    newAcc: DealerAccount[]
  ): boolean => {
    if (existingAcc.length !== newAcc.length) {
      return false;
    }

    // Order-independent comparison: sort IDs before comparing
    const existingIds = existingAcc.map((acc) => acc.id).sort();
    const newIds = newAcc.map((acc) => acc.id).sort();

    return existingIds.every((id, index) => id === newIds[index]);
  };

  const [allAccounts, setAllAccounts] = useState<DealerAccount[]>([]);
  const [allAccountsMapped, setAllAccountsMapped] = useState(false);

  const loadDealerAccounts = (
    accountIds: string[],
    accessToken: string,
    isReloadDealerAccount = false,
    urlAccountId?: string
  ) => {
    const isMultipleAccountsDropdownEnabled =
      ENABLE_MULTIPLE_ACCOUNTS && ENABLE_MULTIPLE_ACCOUNTS_SELECTION_DROPDOWN;

    const accountIdsInUpper = accountIds.map((id) => id.toUpperCase());

    // Prioritize URL account if it exists in the accounts being loaded
    const urlAccountInUpperCase = urlAccountId?.toUpperCase();
    const isUrlAccountBeingLoaded =
      urlAccountInUpperCase &&
      accountIdsInUpper.includes(urlAccountInUpperCase);

    setIsErrorOccurred(false);
    setDealerInfoResponseAndInvalidDealerAccount((prev) => ({
      ...prev,
      dealerInfoLoaderResponseStatus: undefined,
    }));

    if (isMultipleAccountsDropdownEnabled && accountIdsInUpper.length === 0) {
      setDealerAccountInfo({
        ...dealerAccountInfo,
        selectedAccounts: [],
      });
      setDealerInfoResponseAndInvalidDealerAccount(() => ({
        invalidDealerAccountId: undefined,
        dealerInfoLoaderResponseStatus: 200,
        errorOccurredDealerIds: undefined,
        blockedDealerAccountId: false,
      }));
      return;
    }

    dealerInfoLoader(
      XTREME_BFF_SERVICE_URL,
      accountIdsInUpper,
      accessToken,
      i18n.language,
      ENABLE_ACCOUNT_LOCATION_FALLBACK,
      isDealerApiAuthEnabled,
      bffApiHook
    )
      .then(reorderByAccountsIds(accountIdsInUpper))
      .then((dealerAccounts: DealerAccount[]) => {
        if (!allAccountsMapped) {
          setAllAccountsMapped(true);
          setAllAccounts(dealerAccounts);
        }

        setDealerInfoResponseAndInvalidDealerAccount({
          dealerInfoLoaderResponseStatus: 200,
          invalidDealerAccountId: undefined,
          errorOccurredDealerIds: undefined,
          blockedDealerAccountId: false,
        });

        let lastSelectedAccountId = null;

        if (isMultipleAccountsDropdownEnabled) {
          setInitialUrlAccountId(accountIdFromUrl);
        }

        if (isMultipleAccountsDropdownEnabled && isUrlAccountBeingLoaded) {
          lastSelectedAccountId = urlAccountId;
        } else {
          lastSelectedAccountId = SessionStorage.getDealerSelectedAccountId();
        }

        // When multi-account dropdown is enabled, SessionStorage stores IDs as a
        // JSON array (e.g. '["055253"]'). Extract the first element so that the
        // single-account lookups below (firstDealerAccount, isLastSelectedAccountBlocked)
        // correctly resolve the user's persisted selection instead of falling back to
        // the first mapped account (the default) on every page reload.
        let firstSelectedAccountIdForLookup = lastSelectedAccountId;
        if (isMultipleAccountsDropdownEnabled && lastSelectedAccountId) {
          try {
            const parsed = JSON.parse(lastSelectedAccountId);
            if (Array.isArray(parsed) && parsed.length > 0) {
              [firstSelectedAccountIdForLookup] = parsed;
            } else if (typeof parsed === "string") {
              firstSelectedAccountIdForLookup = parsed;
            }
          } catch {
            // keep as-is if not parseable
          }
        }

        const defaultAccountId = SessionStorage.getDefaultAccountId();
        const blockedAccounts = SessionStorage.getBlockedAccounts();
        const lastSelectedDealerAccount = dealerAccounts.find(
          ({ id }) => id === firstSelectedAccountIdForLookup
        );

        const isCorporatePartsManager = user?.is(
          RBACRole.CORPORATE_PARTS_MANAGER
        );

        if (!defaultAccountId && isCorporatePartsManager) {
          const firstValidAccount = dealerAccounts.find(
            (acc) => !acc.dealerInvalid
          );
          if (firstValidAccount) {
            SessionStorage.setDefaultAccountId(firstValidAccount.id);
          }
        }

        if (isCorporatePartsManager) {
          const currentlyBlockedAccounts = dealerAccounts
            .filter((acc) => acc.dealerInvalid)
            .map((acc) => acc.id);
          currentlyBlockedAccounts.forEach((accountId) => {
            if (!blockedAccounts.includes(accountId)) {
              SessionStorage.addBlockedAccount(accountId);
            }
          });
        }

        const isLastSelectedAccountBlocked =
          isCorporatePartsManager &&
          firstSelectedAccountIdForLookup &&
          SessionStorage.getBlockedAccounts().includes(
            firstSelectedAccountIdForLookup
          );

        let firstDealerAccount: DealerAccount;
        if (
          isLastSelectedAccountBlocked &&
          defaultAccountId &&
          isCorporatePartsManager &&
          (!isUrlAccountBeingLoaded || !ENABLE_V3_SALES_DETAILS_INTEGRATION)
        ) {
          const defaultAccount = dealerAccounts.find(
            ({ id }) => id === defaultAccountId
          );
          firstDealerAccount =
            defaultAccount && !defaultAccount.dealerInvalid
              ? defaultAccount
              : dealerAccounts.find((acc) => !acc.dealerInvalid) ??
                dealerAccounts[0];

          if (
            firstDealerAccount &&
            firstDealerAccount.id !== lastSelectedAccountId
          ) {
            const accountToStore = isMultipleAccountsDropdownEnabled
              ? [firstDealerAccount.id]
              : firstDealerAccount.id;
            SessionStorage.setDealerSelectedAccountId(accountToStore);
          }
        } else {
          firstDealerAccount = lastSelectedDealerAccount ?? dealerAccounts[0];
        }

        let selectedAccounts: DealerAccount[] = [];
        if (isMultipleAccountsDropdownEnabled) {
          if (isUrlAccountBeingLoaded) {
            // URL has priority - select only the account from URL
            const urlAccount = dealerAccounts.find(
              (acc) => acc.id.toUpperCase() === urlAccountInUpperCase
            );
            if (urlAccount) {
              selectedAccounts = [urlAccount];
              SessionStorage.setDealerSelectedAccountId([urlAccount.id]);
            }
          } else {
            if (
              lastSelectedAccountId &&
              typeof JSON.parse(lastSelectedAccountId || " ") === "string"
            ) {
              lastSelectedAccountId = `[${lastSelectedAccountId}]`;
            }

            if (lastSelectedAccountId === "ALL") {
              selectedAccounts = dealerAccounts;
            } else if (lastSelectedAccountId) {
              JSON.parse(lastSelectedAccountId || " ").forEach((id: string) => {
                const account = dealerAccounts.find((acc) => acc.id === id);
                if (account) {
                  selectedAccounts.push({ ...account });
                }
              });
            } else if (
              firstDealerAccount &&
              !firstDealerAccount.dealerInvalid
            ) {
              selectedAccounts = [{ ...firstDealerAccount }];
            }
          }
        } else {
          selectedAccounts =
            lastSelectedAccountId === "ALL"
              ? dealerAccounts
              : [firstDealerAccount];
        }

        if (!firstDealerAccount) {
          setDealerInfoResponseAndInvalidDealerAccount({
            dealerInfoLoaderResponseStatus: 404,
            invalidDealerAccountId: accountIdsInUpper[0],
            errorOccurredDealerIds: accountIdsInUpper,
            blockedDealerAccountId: false,
          });
        } else if (
          ENABLE_V3_SALES_DETAILS_INTEGRATION
            ? firstDealerAccount.dealerInvalid
            : dealerAccounts[0].dealerInvalid
        ) {
          setIsErrorOccurred(true);
          setDealerInfoResponseAndInvalidDealerAccount({
            dealerInfoLoaderResponseStatus: 404,
            invalidDealerAccountId: ENABLE_V3_SALES_DETAILS_INTEGRATION
              ? firstDealerAccount.id.toUpperCase()
              : accountIdsInUpper[0],
            errorOccurredDealerIds: accountIdsInUpper,
            blockedDealerAccountId: true,
          });
        } else {
          LocalStorage.setJSONValue("jdRoleActivity", {
            lastVisitedAccountId: firstDealerAccount.id,
          });
        }

        if (firstDealerAccount) {
          if (isReloadDealerAccount) {
            setDealerAccountInfo((prev) => ({
              ...prev,
              selectedAccounts,
              dealerAccount: { ...firstDealerAccount },
              dealerLocation: firstDealerAccount.location.country,
            }));
          } else {
            setDealerAccountInfo({
              mappedAccounts: dealerAccounts,
              selectedAccounts,
              dealerAccount: { ...firstDealerAccount },
              dealerLocation: firstDealerAccount.location.country,
            });
          }
          setHasAccessRestriction(firstDealerAccount.overAllBlock);
          setIsDealerInvalid(firstDealerAccount.dealerInvalid);
          setEmbargoStatus(firstDealerAccount.underEmbargo);
        }
      })
      .catch((error: any) => {
        setIsErrorOccurred(true);
        setDealerAccountInfo({
          mappedAccounts: [],
          selectedAccounts: [],
          dealerAccount: undefined,
          dealerLocation: undefined,
        });
        setDealerInfoResponseAndInvalidDealerAccount(() => ({
          invalidDealerAccountId:
            error?.response?.status === 404 ? accountIds[0] : undefined,
          dealerInfoLoaderResponseStatus: error.response?.status ?? 500,
          errorOccurredDealerIds: accountIds,
          blockedDealerAccountId: false,
        }));
        setHasAccessRestriction(false);
        setEmbargoStatus("");
        setIsDealerInvalid(false);
      });
  };

  const updateSelectedAccounts = (accounts: DealerAccount[]) => {
    if (!areAccountsInfoSame(dealerAccountInfo.selectedAccounts, accounts)) {
      if (accounts.length > 0) {
        setDealerAccountInfo({
          ...dealerAccountInfo,
          selectedAccounts: accounts,
          dealerAccount: { ...accounts[0] },
          dealerLocation: accounts[0].location.country,
        });
      } else if (accounts.length === 0) {
        setDealerAccountInfo({
          ...dealerAccountInfo,
          selectedAccounts: accounts,
        });
      }
    }
  };

  const updateMappedAccounts = (accounts: string[]) => {
    if (authState?.accessToken) {
      loadDealerAccounts(
        accounts,
        authState.accessToken.accessToken,
        false,
        accountIdFromUrl
      );
    }
  };

  const accountIdFromUrl = getAccountIdFromUrl(pathname, search);

  function updateDealerAccount(accountList: Account[], token: string) {
    const accountListHasPrimaryAccount = accountList.filter(
      (account) => account.isPrimaryAccount
    );
    if (accountListHasPrimaryAccount.length > 0) {
      const accountId = accountListHasPrimaryAccount.map(
        (account) => account.id
      )[0];
      loadDealerAccounts([accountId], token, false, accountIdFromUrl);
    } else {
      const accountId = accountList[0].id;
      loadDealerAccounts([accountId], token, false, accountIdFromUrl);
    }
  }

  useEffect(() => {
    // Remove this to complete migration to v2
    if (!ENABLE_DEALER_CONTEXT_V2) return;

    if (
      isCorporateViewEnabled &&
      isAuthorizedForMultipleAccounts(
        user,
        ENABLE_MULTIPLE_ACCOUNTS,
        ENABLE_CORPORATE_VIEW_DPA_ROLE
      ) &&
      (!accountIdFromUrl || !ENABLE_V3_SALES_DETAILS_INTEGRATION)
    ) {
      setDealerInfoResponseAndInvalidDealerAccount((prev) => ({
        ...prev,
        dealerInfoLoaderResponseStatus: 200,
      }));
      return;
    }

    if (isRBACEnabled && user?.isUserWithJDRole() && !accountIdFromUrl) {
      const lastVisitedAccountId = LocalStorage.getJSONValue("jdRoleActivity")
        ?.lastVisitedAccountId as string;
      if (lastVisitedAccountId) {
        loadDealerAccounts(
          [lastVisitedAccountId],
          authState?.accessToken?.accessToken as string,
          false,
          accountIdFromUrl
        );
      } else {
        setDealerInfoResponseAndInvalidDealerAccount((prev) => ({
          ...prev,
          dealerInfoLoaderResponseStatus: 200,
        }));
      }
      return;
    }

    if (!accountIdFromUrl) {
      if (isRBACEnabled && !user) {
        setDealerInfoResponseAndInvalidDealerAccount((prev) => ({
          ...prev,
          dealerInfoLoaderResponseStatus: 401,
        }));
        setIsErrorOccurred(true);
        return;
      }

      if (
        isRBACEnabled &&
        user?.accounts &&
        user.accounts.length > 0 &&
        authState?.isAuthenticated &&
        authState.accessToken?.accessToken
      ) {
        updateDealerAccount(user.accounts, authState.accessToken.accessToken);
        return;
      }

      if (isRBACEnabled && user?.accounts && user.accounts.length === 0) {
        setDealerInfoResponseAndInvalidDealerAccount((prev) => ({
          ...prev,
          dealerInfoLoaderResponseStatus: 200,
        }));
        setDealerAccountInfo({
          ...dealerAccountInfo,
          dealerAccount: { ...initialDealerAccount, id: "undefined" },
        });
        setNoAccountsMappedError(true);
        setIsErrorOccurred(true);
        return;
      }

      setIsErrorOccurred(true);
      return;
    }

    if (
      authState?.isAuthenticated &&
      authState.accessToken?.accessToken &&
      !initialDealerAccount &&
      !isErrorOccurred
    ) {
      if (isRBACEnabled) {
        const hasAccess =
          user?.isUserWithJDRole() ||
          (user?.accounts &&
            user.accounts.length > 0 &&
            userHasAccessToAccount(accountIdFromUrl));
        if (hasAccess) {
          const accountIdsToLoad =
            ENABLE_V3_SALES_DETAILS_INTEGRATION &&
            user?.accounts &&
            user.accounts.length > 1
              ? user.accounts.map((acc) => acc.id)
              : [accountIdFromUrl];

          loadDealerAccounts(
            accountIdsToLoad,
            authState.accessToken.accessToken,
            false,
            accountIdFromUrl
          );
          setIsAuthErrorOccurred(false);
        } else {
          setDealerInfoResponseAndInvalidDealerAccount((prev) => ({
            ...prev,
            dealerInfoLoaderResponseStatus: 401,
          }));
          setIsAuthErrorOccurred(true);
        }
      } else {
        loadDealerAccounts(
          [accountIdFromUrl],
          authState.accessToken.accessToken,
          false,
          accountIdFromUrl
        );
      }
    }
  }, [
    authState,
    initialDealerAccount,
    isErrorOccurredInInitialCall,
    user?.xId,
  ]);

  // Remove `ENABLE_DEALER_CONTEXT_V2` check to complete migration to v2

  const shouldShowLoading =
    (ENABLE_DEALER_CONTEXT_V2 &&
      dealerInfoResponseAndInvalidDealerAccount.dealerInfoLoaderResponseStatus ===
        undefined &&
      !isErrorOccurred) ||
    (dealerAccountInfo.dealerAccount === undefined &&
      !isErrorOccurred &&
      !isAuthErrorOccurred &&
      ENABLE_DEALER_CONTEXT_V2 &&
      (!isCorporateViewEnabled ||
        (isCorporateViewEnabled &&
          !isAuthorizedForMultipleAccounts(
            user,
            ENABLE_MULTIPLE_ACCOUNTS,
            ENABLE_CORPORATE_VIEW_DPA_ROLE
          ))) &&
      !(isRBACEnabled && user?.isUserWithJDRole()));

  if (shouldShowLoading) {
    return <LoadingIndicator color="#367c2b" />;
  }

  return (
    <DealerAccountContext.Provider
      value={{
        allAccounts,
        overAllBlock: hasAccessRestriction,
        underEmbargo: embargoStatus,
        dealerInvalid: isDealerInvalid,
        setIsAccountChanged,
        isAccountChanged,
        dealerLocation: dealerAccountInfo.dealerLocation,
        setDealerLocation: ENABLE_DEALER_CONTEXT_V2
          ? () => {}
          : setDealerLocation,
        dealerAccount: {
          id: accountIdFromUrl as string,
          location: { country: dealerAccountInfo.dealerLocation },
        } as DealerAccount,
        selectedAccounts: [],
        mappedAccounts: [],
        updateMappedAccounts: () => {},
        updateSelectedAccounts: () => {},
        corporateManagerSetupIncomplete:
          dealerAccountInfo.selectedAccounts.length === 0 ||
          dealerAccountInfo.mappedAccounts.length === 0,
        ...(ENABLE_DEALER_CONTEXT_V2
          ? {
              /* Only following fields should be available as value to complete
   migration to v2 */
              selectedAccounts:
                isAuthorizedForMultipleAccounts(
                  user,
                  ENABLE_MULTIPLE_ACCOUNTS
                ) || user?.isUserWithJDRole()
                  ? dealerAccountInfo.selectedAccounts
                  : [],
              mappedAccounts: isAuthorizedForMultipleAccounts(
                user,
                ENABLE_MULTIPLE_ACCOUNTS
              )
                ? dealerAccountInfo.mappedAccounts
                : [],
              updateSelectedAccounts:
                isAuthorizedForMultipleAccounts(
                  user,
                  ENABLE_MULTIPLE_ACCOUNTS
                ) || user?.isUserWithJDRole()
                  ? updateSelectedAccounts
                  : () => {},
              updateMappedAccounts: isAuthorizedForMultipleAccounts(
                user,
                ENABLE_MULTIPLE_ACCOUNTS
              )
                ? updateMappedAccounts
                : () => {},
              dealerAccount: dealerAccountInfo.dealerAccount,
              isErrorOccurred,
              isAuthErrorOccurred,
              noAccountsMappedError,
              reloadDealerAccount: (
                accountId: string | string[] | undefined
              ) => {
                SessionStorage.setDealerSelectedAccountId(accountId || "ALL");
                let accountIdsArray: string[];
                if (typeof accountId === "string") {
                  accountIdsArray = [accountId || (accountIdFromUrl as string)];
                } else if (accountId && Array.isArray(accountId)) {
                  accountIdsArray = accountId;
                } else {
                  accountIdsArray = [accountIdFromUrl as string];
                }
                loadDealerAccounts(
                  accountIdsArray,
                  authState!.accessToken!.accessToken,
                  true
                );
              },
              dealerInfoLoaderResponseStatus:
                dealerInfoResponseAndInvalidDealerAccount.dealerInfoLoaderResponseStatus,
              invalidDealerAccountId:
                dealerInfoResponseAndInvalidDealerAccount.invalidDealerAccountId,
              errorOccurredDealerIds:
                dealerInfoResponseAndInvalidDealerAccount.errorOccurredDealerIds,
              blockerDealerAccountId:
                dealerInfoResponseAndInvalidDealerAccount.blockedDealerAccountId,
              allAccounts,
              initialUrlAccountId,
            }
          : {}),
      }}
    >
      {children}
    </DealerAccountContext.Provider>
  );
};
